import './verificationDocumentListPage.scss';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { VerificationLink } from '../../../components/VerificationLink/VerificationLink';
import useDashboardStore from '../../../store/dashboardStore/useDashboardStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { VerificationStatus } from '../../../store/verificationStore/enums/VerificationStatus';
import { useVerificationLinks } from '../../../store/verificationStore/hooks/useVerificationLinks';
import useVerificationStore from '../../../store/verificationStore/useVerificationStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    computed: { vehicleId },
  } = useDashboardStore();

  const {
    computed: { verifications, progress },
  } = useVerificationStore();

  const { links } = useVerificationLinks(verifications);

  const previousStep = (): void => {
    navigate(`/verifications/${vehicleId}`);
  };

  return (
    <div className="verification-document-list-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header
            data-testid={locator.pages.verificationDocumentList.header.root}
          >
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.verificationDocumentList.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={
                locator.pages.verificationDocumentList.header.drawerButton
              }
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Loan Documents completed percentage"
              data-testid={locator.pages.verificationDocumentList.progress.root}
              value={progress}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.verificationDocumentList.form.root}
              noValidate
            >
              <div className="form-content">
                <h1
                  data-testid={locator.pages.verificationDocumentList.form.h1}
                >
                  {t('VerificationDocumentListPage.title')}
                </h1>
                <h2
                  data-testid={locator.pages.verificationDocumentList.form.h2}
                >
                  {t('VerificationDocumentListPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <ul>
                    {links.map(({ name, status, link }, index) => (
                      <li key={name}>
                        <VerificationLink
                          className={
                            status === VerificationStatus.COMPLETE
                              ? 'complete'
                              : ''
                          }
                          link={link}
                          testId={locator.pages.verificationDocumentList.form.getVerificationLink(
                            index,
                          )}
                        >
                          <div>
                            <div className="title">
                              <h3>{name}</h3>
                              <span>{t(`verificationStatus.${status}`)}</span>
                            </div>
                            {status === VerificationStatus.COMPLETE ? (
                              <CheckCircleRoundedIcon />
                            ) : (
                              <NavigateNextRoundedIcon />
                            )}
                          </div>
                        </VerificationLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <footer
                data-testid={locator.pages.verificationDocumentList.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'VerificationDocumentListPage';
